import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  batchSetDocuments,
  FirestoreService,
} from "../../../../../api/firebase/firestoreService";
import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "../../../../../api/firebase/references";
import type {
  PocAssignmentGroupEntry,
  PocAssignmentGroupsMetadataDoc,
} from "../../../../../data-model/pocAssignmentGroupsMetadata";
import type {
  PocConfigTableMetadataDoc,
  PocConfigTableRow,
} from "../../../../../data-model/pocConfigTableMetadata";
import { RootState } from "../../../../../redux/store/store";
import {
  DEFAULT_POC_ASSIGNMENT_DOC_ID,
  POC_ASSIGNMENT_GROUPS_DOC_ID,
} from "../../pocAssignment/pocAssignmentUtils";
import { usePocAssignmentConfig } from "../../pocAssignment/usePocAssignmentConfig";
import type {
  PocConfigTableRowState,
  PocGroupFormData,
  PocUserFormData,
} from "../data-model/pocAssignmentModel";
import { buildPocAssignmentRunPayload } from "../utils/buildPocAssignmentRunPayload";
import {
  AVAILABILITY_AVAILABLE,
  buildCohortBadgeList,
  buildGroupBadgeListFromNames,
  isAvailabilityId,
  normalizeAvailability,
  POC_CONFIG_TABLE_DOC_ID,
} from "../utils/pocAssignmentConstants";

const withId = (row: PocConfigTableRow): PocConfigTableRowState => ({
  id: `poc-${uuidv4()}`,
  poc: row.poc ?? "",
  group: row.group ?? "",
  batch: Array.isArray(row.batch) ? [...row.batch] : [],
  availability: normalizeAvailability(row.availability),
  cohort: row.cohort ?? "",
});

const cloneRows = (rows: PocConfigTableRowState[]): PocConfigTableRowState[] =>
  rows.map((row) => ({ ...row, batch: [...row.batch] }));

const stripIds = (rows: PocConfigTableRowState[]): PocConfigTableRow[] =>
  rows.map(({ id, ...rest }) => rest);

/**
 * Firestore-backed state for the POC configuration table.
 *
 * Seeds rows from the poc_config_table metadata doc, tracks per-row dirty
 * edits, and on save persists atomically to:
 *  - `/configurations/pharmacy_order_updates/metadata/poc_config_table` (table rows), and
 *  - `/configurations/pharmacy_order_updates/rules/poc_assignment` (generated rules).
 * Adding a group from the Add Group modal also upserts `metadata/poc_assignment_groups`.
 * `assignmentGroupEntries` should mirror that metadata doc so new users get default batches
 * and the Group column options come from the same Firestore path (`poc_assignment_groups`).
 *
 * @param metadataDoc - The fetched metadata doc (null while loading / when absent).
 * @param orgId - Selected health facility id, stamped on the generated rules.
 * @param assignmentGroupEntries - Groups from `metadata/poc_assignment_groups` (names + default batches).
 * @returns Rows, filters, mutation helpers, and save handlers.
 */
export const usePocConfigTable = (
  metadataDoc: PocConfigTableMetadataDoc | null,
  orgId?: string,
  assignmentGroupEntries: PocAssignmentGroupEntry[] = [],
) => {
  const { data: rulesDoc } = usePocAssignmentConfig();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const [rows, setRows] = useState<PocConfigTableRowState[]>([]);
  const [baselineRows, setBaselineRows] = useState<PocConfigTableRowState[]>(
    [],
  );
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [extraGroups, setExtraGroups] = useState<string[]>([]);

  const appliedDocRef = useRef(false);
  /** Cached original doc — preserves id on subsequent saves. */
  const existingDocRef = useRef<PocConfigTableMetadataDoc | null>(null);

  useEffect(() => {
    if (metadataDoc == null || appliedDocRef.current) return;
    appliedDocRef.current = true;
    existingDocRef.current = metadataDoc;
    const seeded = (metadataDoc.data ?? []).map(withId);
    setRows(seeded);
    setBaselineRows(cloneRows(seeded));
  }, [metadataDoc]);

  const dirtyIds = useMemo(() => {
    const dirty = new Set<string>();
    const rowIds = new Set(rows.map((r) => r.id));
    baselineRows.forEach((baselineRow) => {
      if (!rowIds.has(baselineRow.id)) {
        dirty.add(baselineRow.id);
      }
    });
    rows.forEach((row) => {
      const baseline = baselineRows.find((item) => item.id === row.id);
      if (!baseline || JSON.stringify(baseline) !== JSON.stringify(row)) {
        dirty.add(row.id);
      }
    });
    return dirty;
  }, [rows, baselineRows]);

  const isDirty = useCallback((id: string) => dirtyIds.has(id), [dirtyIds]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.poc, row.group, row.cohort, ...row.batch]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [rows, search]);

  const cohortBadgeList = useMemo(
    () => buildCohortBadgeList(rows.map((row) => row.cohort)),
    [rows],
  );

  const metadataGroupNames = useMemo(
    () => assignmentGroupEntries.map((e) => e.group_name).filter(Boolean),
    [assignmentGroupEntries],
  );

  /** Group dropdown options: names from `poc_assignment_groups` plus in-session adds before refetch. */
  const groupBadgeList = useMemo(
    () => buildGroupBadgeListFromNames([...metadataGroupNames, ...extraGroups]),
    [metadataGroupNames, extraGroups],
  );

  /**
   * Applies a partial update to a single row.
   *
   * @param id - Row identifier.
   * @param patch - Fields to update.
   */
  const updateRow = useCallback(
    (id: string, patch: Partial<PocConfigTableRow>) => {
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
    },
    [],
  );

  /**
   * Sets the row's group and replaces batches with those defined for that group
   * in `metadata/poc_assignment_groups` (same source as the group options).
   *
   * @param id - Row id.
   * @param group - Selected group name.
   */
  const updateGroup = useCallback(
    (id: string, group: string) => {
      const trimmed = group.trim();
      const entry = assignmentGroupEntries.find(
        (e) => e.group_name.trim() === trimmed,
      );
      const batchFromMetadata =
        entry?.batches != null && Array.isArray(entry.batches)
          ? [...entry.batches]
          : [];
      updateRow(id, { group, batch: batchFromMetadata });
    },
    [assignmentGroupEntries, updateRow],
  );

  const updateAvailability = useCallback(
    (id: string, availability: string) => {
      if (!isAvailabilityId(availability)) return;
      updateRow(id, { availability: availability === AVAILABILITY_AVAILABLE });
    },
    [updateRow],
  );

  const updateCohort = useCallback(
    (id: string, cohort: string) => updateRow(id, { cohort }),
    [updateRow],
  );

  const updateBatches = useCallback(
    (id: string, batch: string[]) => updateRow(id, { batch }),
    [updateRow],
  );

  /**
   * Appends a new row from the Add New User form.
   *
   * @param data - Selected values from the Add New User modal.
   */
  const addUser = useCallback(
    (data: PocUserFormData) => {
      const trimmedGroup = data.group.trim();
      const entry = assignmentGroupEntries.find(
        (e) => e.group_name.trim() === trimmedGroup,
      );
      const batchFromMetadata =
        entry?.batches != null && Array.isArray(entry.batches)
          ? [...entry.batches]
          : [];

      const newRow: PocConfigTableRowState = {
        id: `poc-${uuidv4()}`,
        poc: data.pocName,
        group: data.group,
        batch: batchFromMetadata,
        availability: data.availability === AVAILABILITY_AVAILABLE,
        cohort: data.cohort,
      };
      setRows((prev) => [...prev, newRow]);
    },
    [assignmentGroupEntries],
  );

  /**
   * Registers a new group from the Add Group modal: upserts
   * `metadata/poc_assignment_groups` in Firestore, then updates local table state.
   *
   * @param data - Group name and selected batches from the modal form.
   * @throws Re-throws when Firestore read/write fails so the modal can stop closing.
   */
  const addGroup = useCallback(
    async (data: PocGroupFormData) => {
      const groupName = data.groupName.trim();
      if (!groupName) return;

      const batches: string[] = [...data.batches];

      try {
        let existingEntries: PocAssignmentGroupEntry[] = [];
        try {
          const doc =
            await FirestoreService.getDocument<PocAssignmentGroupsMetadataDoc>(
              FirestoreDocumentReference.pharmacyOrderUpdatesPocAssignmentGroups(),
            );
          existingEntries = doc.data ?? [];
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          if (!message.includes("does not exist")) {
            throw err;
          }
        }

        const nextData: PocAssignmentGroupEntry[] = [
          ...existingEntries.filter((e) => e.group_name !== groupName),
          { group_name: groupName, batches },
        ];

        const nowIso = new Date().toISOString();
        const groupsPayload: PocAssignmentGroupsMetadataDoc = {
          id: POC_ASSIGNMENT_GROUPS_DOC_ID,
          data: nextData,
          updated_at: nowIso,
          ...(user?.email != null &&
            user.email !== "" && { updated_by: user.email }),
        };

        await FirestoreService.setDocument(
          FirestoreCollectionReference.pharmacyOrderUpdatesMetadata(),
          POC_ASSIGNMENT_GROUPS_DOC_ID,
          groupsPayload,
        );

        setExtraGroups((prev) =>
          prev.includes(groupName) ? prev : [...prev, groupName],
        );

        if (batches.length > 0) {
          setRows((prev) =>
            prev.map((row) =>
              row.group === groupName ? { ...row, batch: [...batches] } : row,
            ),
          );
        }

        toast.success("Group saved");
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to save group. Please try again.",
        );
        throw err;
      }
    },
    [user?.email],
  );

  /**
   * Removes a row from the in-memory table (persist on Save).
   *
   * @param id - Stable row id from `withId` / addUser.
   */
  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  /**
   * Resets table rows to the last saved baseline.
   */
  const refresh = useCallback(() => {
    setRows(cloneRows(baselineRows));
    setSearch("");
  }, [baselineRows]);

  /**
   * Persists the current rows to the metadata doc and the generated rules doc
   * in a single Firestore batch so both succeed or neither is written.
   */
  const saveChanges = useCallback(async () => {
    if (dirtyIds.size === 0 || saving) return;
    setSaving(true);
    try {
      const metadataPayload: PocConfigTableMetadataDoc = {
        id: existingDocRef.current?.id ?? POC_CONFIG_TABLE_DOC_ID,
        data: stripIds(rows),
      };
      const rulesPayload = buildPocAssignmentRunPayload(
        rows,
        rulesDoc,
        user?.email ?? undefined,
        orgId,
      );

      await batchSetDocuments([
        {
          collectionPath:
            FirestoreCollectionReference.pharmacyOrderUpdatesMetadata(),
          docId: POC_CONFIG_TABLE_DOC_ID,
          data: metadataPayload,
        },
        {
          collectionPath:
            FirestoreCollectionReference.pharmacyOrderUpdatesRules(),
          docId: DEFAULT_POC_ASSIGNMENT_DOC_ID,
          data: rulesPayload,
        },
      ]);

      existingDocRef.current = metadataPayload;
      setBaselineRows(cloneRows(rows));
      toast.success("Changes saved");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }, [dirtyIds.size, saving, rows, rulesDoc, user?.email, orgId]);

  return {
    rows,
    filteredRows,
    cohortBadgeList,
    groupBadgeList,
    search,
    setSearch,
    dirtyCount: dirtyIds.size,
    isDirty,
    updateGroup,
    updateAvailability,
    updateCohort,
    updateBatches,
    addUser,
    addGroup,
    removeRow,
    refresh,
    saveChanges,
    saving,
  };
};
